/**
 * Centralized null-vs-zero display formatters for the planner.
 *
 * NULL SEMANTICS RULE:
 * - Backend null  → display dash "—"  (data unavailable)
 * - Numeric 0     → display "0 EUR"   (legitimate zero value)
 * - NaN/undefined → display dash "—"  (guard against bad data)
 *
 * All planner components that show economics or area MUST use these formatters
 * so the null-vs-zero distinction cannot regress silently per component.
 *
 * LOCALE → CURRENCY MAPPING (canonical, owned here):
 *   en-US → EUR  (Finnish forestry app; EUR regardless of display locale)
 *   fi-FI → EUR
 *   sv-SE → SEK
 *   es-ES → EUR
 *   (default fallback) → EUR
 *
 * This mapping is used by formatPlannerCurrency and friends when no explicit
 * currency code is supplied.  Override by passing the `currency` argument.
 */

/** The dash displayed when a value is unavailable (null/undefined). */
export const PLANNER_NULL_DISPLAY = '—';

/**
 * Resolve the ISO 4217 currency code that corresponds to a BCP-47 locale tag.
 *
 * Mapping:
 *   en-US → EUR  (Finnish forestry app; EUR regardless of display locale)
 *   fi-FI → EUR
 *   sv-SE → SEK
 *   es-ES → EUR
 *   (default)  → EUR
 */
export function localeToCurrency(locale: string): string {
  switch (locale) {
    case 'en-US': return 'EUR';
    case 'sv-SE': return 'SEK';
    case 'fi-FI': return 'EUR';
    case 'es-ES': return 'EUR';
    default:      return 'EUR';
  }
}

/**
 * Format a nullable currency value for display using Intl.NumberFormat with
 * style:'currency'.  The currency is resolved from the locale by default but
 * can be overridden explicitly.
 *
 * @param value    - Number (including 0) or null/undefined
 * @param locale   - BCP-47 locale for number formatting (default 'fi-FI')
 * @param currency - ISO 4217 currency code; defaults to localeToCurrency(locale)
 * @returns Formatted string like "1 234 €" / "$1,234" or "—" when unavailable.
 */
export function formatPlannerCurrency(
  value: number | null | undefined,
  locale: string = 'fi-FI',
  currency?: string,
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return PLANNER_NULL_DISPLAY;
  }
  const resolvedCurrency = currency ?? localeToCurrency(locale);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: resolvedCurrency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(value));
}

/**
 * Format a nullable currency-per-hectare value for display.
 * Appends "/ha" to the locale-aware currency string.
 *
 * @param value    - Number (including 0) or null/undefined
 * @param locale   - BCP-47 locale for number formatting (default 'fi-FI')
 * @param currency - ISO 4217 currency code; defaults to localeToCurrency(locale)
 * @returns Formatted string like "1 234 €/ha" or "—" when unavailable.
 */
export function formatPlannerCurrencyPerHa(
  value: number | null | undefined,
  locale: string = 'fi-FI',
  currency?: string,
): string {
  const base = formatPlannerCurrency(value, locale, currency);
  if (base === PLANNER_NULL_DISPLAY) return PLANNER_NULL_DISPLAY;
  return `${base}/ha`;
}

/**
 * Format a nullable percentage value for display.
 * Uses Intl.NumberFormat for locale-aware decimal separators and prepends
 * an explicit +/- sign to make gain/loss immediately obvious.
 *
 * @param value    - Number (e.g. 3.5 for 3.5%) or null/undefined
 * @param locale   - BCP-47 locale (default 'fi-FI')
 * @param decimals - Decimal places (default 1)
 * @returns e.g. "+3,5 %" (fi-FI) / "+3.5 %" (en-US) or "—" when unavailable.
 */
export function formatPlannerPercent(
  value: number | null | undefined,
  locale: string = 'fi-FI',
  decimals: number = 1,
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return PLANNER_NULL_DISPLAY;
  }
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
  return `${value > 0 ? '+' : ''}${formatted} %`;
}

/**
 * Format a nullable number to a fixed number of decimal places using
 * locale-aware formatting.  Returns an en-dash "–" when the value is
 * null/undefined (matching the en-dash convention used in comparison tables).
 *
 * @param value    - Number or null/undefined
 * @param locale   - BCP-47 locale (default 'fi-FI')
 * @param decimals - Decimal places (default 0)
 * @returns e.g. "1 234" (fi-FI) / "1,234" (en-US) or "–" when unavailable.
 */
export function formatPlannerNumber(
  value: number | null | undefined,
  locale: string = 'fi-FI',
  decimals: number = 0,
): string {
  if (value === undefined || value === null) return '–';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(decimals === 0 ? Math.round(value) : value);
}

/**
 * Format a nullable area value for display using locale-aware decimal formatting.
 *
 * @param value     - Number (including 0) or null/undefined
 * @param precision - Decimal places (default 2)
 * @param locale    - BCP-47 locale string (default 'fi-FI')
 * @returns Formatted string like "1,23" (fi) / "1.23" (en) or "—" when unavailable.
 */
export function formatPlannerArea(
  value: number | null | undefined,
  precision: number = 2,
  locale: string = 'fi-FI',
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return PLANNER_NULL_DISPLAY;
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value);
}

/**
 * Map an i18next language code ('fi' | 'en' | 'sv' | 'es') to a BCP-47 locale tag
 * suitable for Intl APIs.
 *
 * i18next stores the short code; Intl works best with full tags.
 */
export function languageToBcp47(language: string): string {
  switch (language) {
    case 'fi': return 'fi-FI';
    case 'sv': return 'sv-SE';
    case 'es': return 'es-ES';
    default:   return 'en-US';
  }
}

/**
 * Safely sum nullable numbers for period totals.
 * Returns null only when ALL inputs are null (i.e. no data at all).
 * Returns the sum of non-null values when at least one is non-null.
 *
 * This preserves the distinction:
 *   - All null  → total is null  → display "—"
 *   - Some null → treat null as 0 for the sum → display computed total
 */
/**
 * Build a concise unavailable explanation for a planner economics cell.
 *
 * Combines revenue and cost unavailable reasons into one tooltip line
 * suitable for the "net" column. For income/cost columns, use the
 * specific reason directly.
 */
export function buildUnavailableTooltip(
  column: 'income' | 'cost' | 'net',
  revenueReason: string | null | undefined,
  costReason: string | null | undefined,
): string | undefined {
  if (column === 'income') return revenueReason ?? undefined;
  if (column === 'cost') return costReason ?? undefined;
  // net: combine both
  const parts: string[] = [];
  if (revenueReason) parts.push(revenueReason);
  if (costReason) parts.push(costReason);
  return parts.length > 0 ? parts.join('; ') : undefined;
}

export function sumNullable(values: (number | null)[]): number | null {
  const nonNull = values.filter((v): v is number => v !== null);
  if (nonNull.length === 0) return null;
  return nonNull.reduce((a, b) => a + b, 0);
}
